import {
  CheckCircleOutline,
  DeleteOutline,
  ErrorOutline,
  MoreVert,
  RadioButtonUnchecked,
  RefreshOutlined,
  SearchOutlined,
  ThumbUpAlt,
} from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  SEQUENCE_ACTION_TYPE_AUTO_AGENT,
  SEQUENCE_ACTION_TYPE_EMAIL,
} from "../../../constants";
import {
  formatStep,
  hostname,
  parseError,
  trimmedString,
} from "../../../utils";
import LoadingButton from "../../../components/LoadingButton";
import BulkActionFailureDialog from "../dialogs/BulkActionFailureDialog";
import ExecutionResultDialog from "../dialogs/ExecutionResultDialog";
import SequenceExecutionEditor from "../SequenceExecutionEditor";
import SequenceExecutionPreview from "../SequenceExecutionPreview";

interface SequenceActionsProps {
  companyId: string;
  sequence?: any;
  getToken: () => Promise<string>;
  eventListener?: Function;
  showTitle?: boolean;
  initialFilter?: string;
  showAllSequences?: boolean; // If true, show executions from all sequences (dashboard view)
}

export default ({
  companyId,
  sequence,
  getToken,
  eventListener,
  showTitle = true,
  initialFilter = "unapproved",
  showAllSequences = false,
}: SequenceActionsProps) => {
  const [executions, setExecutions] = useState<Array<any>>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openExecutionMenu, setOpenExecutionMenu] = useState<any>(null);
  const [selectedExecution, setSelectedExecution] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [executionState, setExecutionState] = useState(initialFilter);
  const [sequenceStepId, setSequenceStepId] = useState("all");
  const [availableSteps, setAvailableSteps] = useState<Array<any>>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkExecuting, setBulkExecuting] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [failureDialogOpen, setFailureDialogOpen] = useState(false);
  const [failureData, setFailureData] = useState<{
    actionType: "approve" | "execute" | "delete";
    failures: Array<{
      executionId: string;
      memberName?: string;
      memberEmail?: string;
      error: string;
    }>;
    successCount: number;
    totalCount: number;
  } | null>(null);
  const [executionResultDialogOpen, setExecutionResultDialogOpen] = useState(false);
  const [executionResultData, setExecutionResultData] = useState<{
    actionType: "approve" | "execute" | "delete";
    successes: Array<{
      executionId: string;
      memberName?: string;
      memberEmail?: string;
    }>;
    failures: Array<{
      executionId: string;
      memberName?: string;
      memberEmail?: string;
      error: string;
    }>;
    totalCount: number;
  } | null>(null);
  const [rebuildingExecution, setRebuildingExecution] = useState<string | null>(null);
  const [rerunningExecution, setRerunningExecution] = useState<string | null>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = executions.map((execution) => execution.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  // Debounce search query
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set a new timer to update debounced search after 500ms
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(0); // Reset to first page when search changes
    }, 500);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const refreshExecutions = async () => {
    setRefreshing(true);
    const currentToken = await getToken();

    // Determine which URL to use based on context
    let url;
    if (showAllSequences) {
      // Dashboard view: show all executions from all sequences
      url = `${hostname}/sequences/executions/all`;
    } else if (sequence?.id && sequence.id.trim()) {
      // Sequence editor view: show executions only for this sequence
      url = `${hostname}/sequences/${sequence.id}/executions`;
    } else {
      // New sequence that hasn't been saved yet: no executions to show
      setExecutions([]);
      setTotal(0);
      setRefreshing(false);
      return;
    }

    axios
      .get(url, {
        params: {
          companyId,
          page: page + 1,
          pageSize,
          executionState,
          sequenceStepId: sequenceStepId !== "all" ? sequenceStepId : undefined,
          search: debouncedSearchQuery || undefined,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setExecutions(res.data.items || []);
        setTotal(res.data.total || 0);

        // Extract unique steps from the executions only if showing all sequences
        // (for specific sequences, we fetch steps separately)
        if (showAllSequences) {
          const stepsMap = new Map();
          res.data.items.forEach((exec: any) => {
            if (exec.step && exec.step.id) {
              stepsMap.set(exec.step.id, exec.step);
            }
          });
          const extractedSteps = Array.from(stepsMap.values()).sort((a, b) => a.order - b.order);
          console.log("Extracted steps from executions:", extractedSteps);
          setAvailableSteps(extractedSteps);
        }
      })
      .catch((err) => {
        if (eventListener) {
          eventListener({
            type: "SET_ERROR",
            error: parseError(err),
          });
        }
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const loadExecutions = async () => {
    setLoading(true);
    await refreshExecutions();
    setLoading(false);
  };

  const fetchSequenceSteps = async () => {
    // For specific sequences, use steps if already provided in the sequence object
    if (sequence?.steps && sequence.steps.length > 0) {
      const steps = sequence.steps
        .filter((s: any) => !s.deleted_date)
        .sort((a: any, b: any) => a.order - b.order);
      console.log("Setting available steps from sequence object:", steps);
      setAvailableSteps(steps);
      return;
    }

    // Otherwise, fetch sequence steps if viewing a specific sequence
    if (sequence?.id && !showAllSequences) {
      const currentToken = await getToken();
      try {
        const response = await axios.get(
          `${hostname}/sequences/${sequence.id}`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${currentToken}` },
          }
        );
        if (response.data.sequence?.steps) {
          const steps = response.data.sequence.steps
            .filter((s: any) => !s.deleted_date)
            .sort((a: any, b: any) => a.order - b.order);
          console.log("Setting available steps from API:", steps);
          setAvailableSteps(steps);
        } else {
          console.log("No steps found in API response");
        }
      } catch (err) {
        // If fetching sequence steps fails, fall back to extracting from executions
        console.error("Failed to fetch sequence steps:", err);
      }
    } else {
      console.log("Not fetching steps - showAllSequences:", showAllSequences, "sequence.id:", sequence?.id);
    }
  };

  const deleteExecutions = async (executionIds: string[]) => {
    setBulkDeleting(true);
    const currentToken = await getToken();
    
    // Use the appropriate endpoint based on context
    const url = sequence?.id
      ? `${hostname}/sequences/${sequence.id}/executions/delete`
      : `${hostname}/sequences/executions/delete`;
    
    try {
      const response = await axios.post(
        url,
        { executionIds, companyId },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      
      const result = response.data;
      
      setSelected([]);
      refreshExecutions();
      
      // Show failure dialog if there are any failures
      if (result.failures && result.failures.length > 0) {
        setFailureData({
          actionType: "delete",
          failures: result.failures,
          successCount: result.successCount,
          totalCount: result.totalCount
        });
        setFailureDialogOpen(true);
      }
      
    } catch (err) {
      // If the new endpoint fails, fall back to showing a general error
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const approveExecutions = async (executionIds: string[]) => {
    setBulkApproving(true);
    const currentToken = await getToken();
    
    const url = sequence?.id
      ? `${hostname}/sequences/${sequence.id}/executions/approve`
      : `${hostname}/sequences/executions/approve`;
    
    try {
      const response = await axios.post(
        url,
        { executionIds, companyId },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      
      const result = response.data;

      // Don't clear selections after approve - user often wants to execute same items next
      refreshExecutions();

      // Show failure dialog if there are any failures
      if (result.failures && result.failures.length > 0) {
        setFailureData({
          actionType: "approve",
          failures: result.failures,
          successCount: result.successCount,
          totalCount: result.totalCount
        });
        setFailureDialogOpen(true);
      }
      
    } catch (err) {
      // If the new endpoint fails, fall back to showing a general error
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      }
    } finally {
      setBulkApproving(false);
    }
  };

  const executeExecutions = async (executionIds: string[]) => {
    setBulkExecuting(true);
    const currentToken = await getToken();

    const url = sequence?.id
      ? `${hostname}/sequences/${sequence.id}/executions/execute`
      : `${hostname}/sequences/executions/execute`;

    try {
      const response = await axios.post(
        url,
        { executionIds, companyId },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      const result = response.data;

      setSelected([]);
      refreshExecutions();

      // Always show result dialog with successes and failures
      setExecutionResultData({
        actionType: "execute",
        successes: result.successes || [],
        failures: result.failures || [],
        totalCount: result.totalCount || executionIds.length
      });
      setExecutionResultDialogOpen(true);

    } catch (err) {
      // If the new endpoint fails, fall back to showing a general error
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      }
    } finally {
      setBulkExecuting(false);
    }
  };

  const rebuildExecution = async (executionId: string) => {
    setRebuildingExecution(executionId);
    const currentToken = await getToken();

    try {
      await axios.post(
        `${hostname}/sequences/executions/${executionId}/rebuild`,
        { companyId },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      refreshExecutions();

      if (eventListener) {
        eventListener({
          type: "SET_SUCCESS",
          message: "Execution content regenerated successfully",
        });
      }
    } catch (err) {
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      }
    } finally {
      setRebuildingExecution(null);
      setAnchorEl(null);
      setOpenExecutionMenu(null);
      setSelectedExecution(null);
    }
  };

  const rerunExecution = async (executionId: string, regenerateContent: boolean = false) => {
    setRerunningExecution(executionId);
    const currentToken = await getToken();

    try {
      await axios.post(
        `${hostname}/sequences/executions/${executionId}/rerun`,
        { companyId, regenerateContent },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      refreshExecutions();

      if (eventListener) {
        eventListener({
          type: "SET_SUCCESS",
          message: "Execution reset successfully - ready to be approved and sent",
        });
      }
    } catch (err) {
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      }
    } finally {
      setRerunningExecution(null);
      setAnchorEl(null);
      setOpenExecutionMenu(null);
      setSelectedExecution(null);
    }
  };

  const getExecutionStatusIcon = (execution: any) => {
    if (execution.executed && execution.failed) {
      return <ErrorOutline color="error" />;
    } else if (execution.executed) {
      return <CheckCircleOutline color="success" />;
    } else {
      return <RadioButtonUnchecked color="disabled" />;
    }
  };

  const getExecutionStatusText = (execution: any) => {
    if (execution.executed && execution.failed) {
      return "Failed";
    } else if (execution.executed) {
      return "Sent";
    } else if (execution.approved) {
      return "Approved";
    } else {
      return "Pending";
    }
  };

  useEffect(() => {
    // Refresh executions whether sequence is provided or not
    refreshExecutions();
  }, [sequence?.id, page, pageSize, executionState, sequenceStepId, debouncedSearchQuery]);

  useEffect(() => {
    console.log("SequenceActions mounted/updated - sequence:", sequence);
    console.log("Available steps:", availableSteps);
    // Load executions on mount whether sequence is provided or not
    loadExecutions();
    // Fetch sequence steps for the dropdown
    fetchSequenceSteps();
  }, [sequence?.id, sequence?.steps?.length]);

  // Component no longer requires a sequence - it can show all executions

  return (
    <Box>
      {showTitle && (
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Sequence Actions
        </Typography>
      )}

      {/* Filter and Actions */}
      <Box
        sx={{
          my: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search by email, name, subject, or content..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={executionState}
            label="Status"
            onChange={(e) => {
              setExecutionState(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="unapproved">Pending Approval</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="executed">Sent</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Step</InputLabel>
          <Select
            value={sequenceStepId}
            label="Step"
            onChange={(e) => {
              setSequenceStepId(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Steps</MenuItem>
            {availableSteps.map((step) => (
              <MenuItem key={step.id} value={step.id}>
                {step.stepName || step.subject
                  ? trimmedString(`#${step.order} ${step.stepName || step.subject}`, 50)
                  : `#${step.order}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selected.length > 0 && (
          <>
            <LoadingButton
              variant="contained"
              startIcon={<ThumbUpAlt />}
              onClick={() => approveExecutions(selected)}
              disabled={refreshing}
              loading={bulkApproving}
            >
              Approve ({selected.length})
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              startIcon={<CheckCircleOutline />}
              onClick={() => executeExecutions(selected)}
              disabled={refreshing}
              loading={bulkExecuting}
            >
              Execute ({selected.length})
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={() => deleteExecutions(selected)}
              disabled={refreshing}
              loading={bulkDeleting}
            >
              Delete ({selected.length})
            </LoadingButton>
          </>
        )}
      </Box>

      <Paper variant="outlined">
        {refreshing && <LinearProgress />}
        {!refreshing && <div style={{ height: 4 }}></div>}

        <IconButton
          onClick={refreshExecutions}
          style={{ float: "right", marginRight: 5 }}
        >
          <RefreshOutlined />
        </IconButton>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 && selected.length < executions.length
                    }
                    checked={
                      executions.length > 0 &&
                      selected.length === executions.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                {showAllSequences && <TableCell>Sequence</TableCell>}
                <TableCell>Member</TableCell>
                <TableCell>Step</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {executions.map((execution) => {
                const isItemSelected = isSelected(execution.id);
                return (
                  <TableRow
                    key={execution.id}
                    hover
                    onClick={(event) => handleClick(event, execution.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox color="primary" checked={isItemSelected} />
                    </TableCell>
                    {showAllSequences && (
                      <TableCell>
                        <Typography variant="body2">
                          {execution.sequence?.name || "-"}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      {execution.member?.firstName} {execution.member?.lastName}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {execution.member?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {execution.step ? formatStep(execution.step) : "-"}
                        {execution.step?.actionType === SEQUENCE_ACTION_TYPE_AUTO_AGENT &&
                          execution.platformActionData?.autoAgentChannel && (
                            <Chip
                              label={execution.platformActionData.autoAgentChannel}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: "capitalize" }}
                            />
                          )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {execution.step?.actionType === SEQUENCE_ACTION_TYPE_EMAIL
                        ? trimmedString(execution.subject || "", 50)
                        : execution.step?.stepName || execution.step?.subject || "-"}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getExecutionStatusIcon(execution)}
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="body2">
                            {getExecutionStatusText(execution)}
                          </Typography>
                          {execution.executed &&
                            execution.failed &&
                            execution.failureReason && (
                              <Typography
                                variant="caption"
                                color="error"
                                display="block"
                              >
                                {execution.failureReason}
                              </Typography>
                            )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {moment(execution.created_date).format("MMM D, h:mm A")}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setAnchorEl(e.currentTarget);
                          setOpenExecutionMenu(execution.id);
                          setSelectedExecution(execution);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      {/* Execution Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={openExecutionMenu === selectedExecution?.id}
        onClose={() => {
          setAnchorEl(null);
          setOpenExecutionMenu(null);
          setSelectedExecution(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setPreviewOpen(true);
            setAnchorEl(null);
            setOpenExecutionMenu(null);
          }}
        >
          Preview
        </MenuItem>
        <MenuItem
          onClick={() => {
            setEditorOpen(true);
            setAnchorEl(null);
            setOpenExecutionMenu(null);
          }}
        >
          Edit
        </MenuItem>
        {/* Rebuild - regenerate content for pending executions */}
        {selectedExecution && !selectedExecution.executed && (
          <MenuItem
            onClick={() => {
              if (selectedExecution) {
                rebuildExecution(selectedExecution.id);
              }
            }}
            disabled={rebuildingExecution === selectedExecution?.id}
          >
            {rebuildingExecution === selectedExecution?.id ? "Rebuilding..." : "Rebuild Content"}
          </MenuItem>
        )}
        {/* Rerun - reset executed/failed steps to try again */}
        {selectedExecution && selectedExecution.executed && (
          <MenuItem
            onClick={() => {
              if (selectedExecution) {
                rerunExecution(selectedExecution.id, true);
              }
            }}
            disabled={rerunningExecution === selectedExecution?.id}
          >
            {rerunningExecution === selectedExecution?.id ? "Resetting..." : "Rerun Step"}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (selectedExecution) {
              deleteExecutions([selectedExecution.id]);
            }
            setAnchorEl(null);
            setOpenExecutionMenu(null);
            setSelectedExecution(null);
          }}
          sx={{ color: "error.main" }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Preview Dialog */}
      <SequenceExecutionPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        execution={selectedExecution}
        companyId={companyId}
        getToken={getToken}
      />

      {/* Editor Dialog */}
      <SequenceExecutionEditor
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setSelectedExecution(null);
          refreshExecutions();
        }}
        execution={selectedExecution}
        companyId={companyId}
        getToken={getToken}
      />

      {/* Bulk Action Failure Dialog */}
      <BulkActionFailureDialog
        open={failureDialogOpen}
        onClose={() => {
          setFailureDialogOpen(false);
          setFailureData(null);
        }}
        actionType={failureData?.actionType || "approve"}
        failures={failureData?.failures || []}
        successCount={failureData?.successCount || 0}
        totalCount={failureData?.totalCount || 0}
      />

      {/* Execution Result Dialog */}
      <ExecutionResultDialog
        open={executionResultDialogOpen}
        onClose={() => {
          setExecutionResultDialogOpen(false);
          setExecutionResultData(null);
        }}
        actionType={executionResultData?.actionType || "execute"}
        successes={executionResultData?.successes || []}
        failures={executionResultData?.failures || []}
        totalCount={executionResultData?.totalCount || 0}
      />
    </Box>
  );
};
